import { SVGAttributes, useRef } from 'react';

import styled from 'styled-components';

import { Tooltip } from '@components/tooltip';
import { Color, theme } from '@styles/theme';
import { Transient } from '@utils/types';

export const iconGradients = {
  aiGradientTeal100: { from: theme.color.elementTeal100, to: theme.color.elementViolet100 },
} as const;

export type IconGradient = keyof typeof iconGradients;

export type BaseIconProps = SVGAttributes<any> & {
  color?: Color | IconGradient;
  size?: number | string;
  tooltip?: string;
  rotateDegrees?: number;
  flipHorizontal?: boolean;
  dataTestid?: string;
};

export type IconProps = Omit<BaseIconProps, 'viewBox' | 'width' | 'height'>;
export type IconSmallProps = Omit<IconProps, 'size'>;

let nextGradientId = 0;

const VIEWBOX_SIZE = 24;
const VIEWBOX_CENTER = VIEWBOX_SIZE / 2;
const GRADIENT_ANGLE = 135;

const getGradientCoords = () => {
  const rad = (GRADIENT_ANGLE * Math.PI) / 180;
  return {
    x1: VIEWBOX_CENTER - VIEWBOX_CENTER * Math.sin(rad),
    y1: VIEWBOX_CENTER + VIEWBOX_CENTER * Math.cos(rad),
    x2: VIEWBOX_CENTER + VIEWBOX_CENTER * Math.sin(rad),
    y2: VIEWBOX_CENTER - VIEWBOX_CENTER * Math.cos(rad),
  };
};

const isGradient = (color: Color | IconGradient): color is IconGradient => {
  return color in iconGradients;
};

export function BaseIcon(props: BaseIconProps) {
  const {
    size = 24,
    color,
    tooltip,
    children,
    style,
    rotateDegrees,
    flipHorizontal,
    dataTestid,
    ...rest
  } = props;

  const gradient = color && isGradient(color) ? iconGradients[color] : undefined;

  const gradientIdRef = useRef<string | null>(null);
  if (gradient && gradientIdRef.current === null) {
    nextGradientId += 1;
    gradientIdRef.current = `icon-gradient-${nextGradientId}`;
  }

  const gradientId = gradient ? gradientIdRef.current : null;
  const gradientCoords = gradient ? getGradientCoords() : undefined;

  const content = (
    <Svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      $rotateDegrees={rotateDegrees}
      $flipHorizontal={flipHorizontal}
      $gradientId={gradientId}
      {...rest}
      data-testid={dataTestid}
      style={{
        ...style,
        '--icon-color': !gradient && color ? theme.color[color as Color] : undefined,
      }}
    >
      {gradient && gradientCoords && (
        <defs>
          <linearGradient
            id={gradientId as string}
            x1={gradientCoords.x1}
            y1={gradientCoords.y1}
            x2={gradientCoords.x2}
            y2={gradientCoords.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={gradient.from} />
            <stop offset="1" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      )}
      {children}
    </Svg>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return content;
}

type SvgStyleProps = Transient<BaseIconProps, 'rotateDegrees' | 'flipHorizontal'> & {
  $gradientId?: string | null;
};

const Svg = styled.svg<SvgStyleProps>(({ $rotateDegrees, $flipHorizontal, $gradientId }) => ({
  transition: 'color 150ms ease-out',
  color: 'var(--icon-color, currentColor)',
  flexShrink: 0,
  path: {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transition: $rotateDegrees !== undefined ? 'transform 200ms ease-out' : undefined,
    transform: [
      $flipHorizontal ? 'scaleX(-1)' : undefined,
      typeof $rotateDegrees === 'number' ? `rotate(${$rotateDegrees}deg)` : undefined,
    ]
      .filter(Boolean)
      .join(' '),
  },
  ...($gradientId && {
    '& [fill="currentColor"]': {
      fill: `url(#${$gradientId})`,
    },
  }),
}));
